import { useState } from "react";

import styled from "styled-components";
import { getFilesFromEvent } from "utils/upload/getFilesFromEvent";
import { uploadDroppedItems } from "utils/upload/uploadDroppedItems";

import { IShapefileNode } from "models/projects";

import { EditableShapefileFolderNode } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager";
import { useStructuredNodeContext } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/StructuredNodeContext";
import { setInEditMode } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/reducer";
import {
  ActionButton,
  FlexItem
} from "components/project/layers/components/layer-managers/shared";
import { useRightClickContextMenu } from "components/project/layers/components/layer-managers/shared/hooks/useRightClickContextMenu";
import { ShapefileFolderActions } from "components/project/layers/components/layer-managers/shared/shapefile-folder-actions/ShapefileFolderActions";
import { SHAPEFILE_FOLDER_TYPE } from "components/project/layers/hooks/project-layer-tree/useProjectLayerTree.types";
import { useProjectDispatch } from "components/project/projects/context";
import { useCreateProjectShapefileFolder } from "components/project/shapefiles/hooks/createProjectShapefileFolder/useCreateProjectShapefileFolder";
import { useUploadProjectShapefileList } from "components/project/shapefiles/hooks/useUploadProjectShapefileList";
import { useDeleteProjectShapefileFolderMutation } from "components/project/shapefiles/mutations";

export default function SystemFolderNode({ node, hasActions = true }) {
  const projectDispatch = useProjectDispatch();
  const { openContextMenu, contextMenuObject } = useRightClickContextMenu();

  const [{ inEditMode }, nodeDispatch] = useStructuredNodeContext();
  const [canDrop, setCanDrop] = useState(false);
  const [tempFoldersLength, setTempFoldersLength] = useState(0);

  const createProjectFolder = useCreateProjectShapefileFolder({
    projectId: SHAPEFILE_FOLDER_TYPE.SYSTEM
  });

  const deleteFolder = useDeleteProjectShapefileFolderMutation({
    projectId: SHAPEFILE_FOLDER_TYPE.SYSTEM
  });

  const uploadShapefileList = useUploadProjectShapefileList();

  const handleCreateFolder = async () => {
    setTempFoldersLength((prev) => prev + 1);

    try {
      await createProjectFolder.mutateAsync({
        parentId: sf.shapefileNodeId,
        order: node.children.length + tempFoldersLength
      });
    } finally {
      setTempFoldersLength((prev) => prev - 1);
    }
  };

  const sf = node as IShapefileNode;

  return (
    <LeafTitle
      canDrop={canDrop}
      onContextMenu={openContextMenu}
      onDragOver={(e) => {
        e.preventDefault();
        const files = getFilesFromEvent(e);
        setCanDrop(files.length > 0);
      }}
      // A dropped folder has to be read before it can be uploaded, and the browser
      // empties the dropped list as soon as this handler returns, so the list is
      // handed over before the first await
      onDrop={async (ev) => {
        ev.preventDefault();
        setCanDrop(false);
        await uploadDroppedItems({
          transfer: ev.dataTransfer,
          shapefileNodeId: sf.shapefileNodeId,
          upload: uploadShapefileList.mutate
        });
      }}
      onDragEnter={(e) => {
        const files = getFilesFromEvent(e);
        setCanDrop(files.length > 0);
      }}
      onDragLeave={() => {
        setCanDrop(false);
      }}>
      <EditableShapefileFolderNode projectId={SHAPEFILE_FOLDER_TYPE.SYSTEM} node={node} />
      {hasActions && !inEditMode && (
        <FlexItem>
          <ShapefileFolderActions
            addFolder={handleCreateFolder}
            deleteFolder={() => {
              deleteFolder.mutate({
                shapefileNodeId: sf.shapefileNodeId,
                projectId: "system",
                folderNode: node
              });
            }}
            hasSelectItems={false} // Only available in project tree
            setIsRenaming={() => {
              setInEditMode(nodeDispatch, true, (isDraggable) =>
                projectDispatch({ payload: { isTreeDraggable: isDraggable } })
              );
            }}
            folderName={node.title}
            shapefileNodeId={sf.shapefileNodeId}
            contextMenu={contextMenuObject}
          />
        </FlexItem>
      )}
    </LeafTitle>
  );
}

const LeafTitle = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: center;
  justify-content: space-between;

  .drag-container {
    position: absolute;
    opacity: 0.5;
    left: -20px;
    top: 4px;
    color: #a2aaad;
    cursor: move;
  }

  ${ActionButton} {
    svg {
      opacity: 0;
    }
  }

  &:hover,
  &.in-edit-mode {
    svg {
      opacity: 1;
    }
  }

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: center;
  justify-content: space-between;

  border: ${(props) => (props.canDrop ? "1px dashed green" : "none")};

  input[type="text"] {
    height: 24px;
    outline: none;
  }

  input[type="text"]:focus {
    outline: none 0;
  }

  label {
    margin-left: 5px !important;
  }
`;
